import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Stack,
    Alert,
    CircularProgress,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../stores';
import { DriverFormData } from '../../stores/DriverStore';
import { Vehicle } from '../../stores/VehicleStore';

interface DriverFormProps {
    selectedVehicle?: Vehicle | null;
    onSuccess?: () => void;
}

const DriverRegistrationForm: React.FC<DriverFormProps> = observer(({ selectedVehicle, onSuccess }) => {
    const { driverStore, vehicleStore } = useStore();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState<DriverFormData>({
        user: {
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            phone_number: '',
            profile_picture_url: ''
        },
        license_number: '',
        license_expiry: '',
        status: 'ACTIVE',
        vehicle: undefined
    });

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (selectedVehicle) {
            setFormData(prev => ({ ...prev, vehicle: selectedVehicle.id }));
            if (validationErrors.vehicle) {
                setValidationErrors(prev => ({ ...prev, vehicle: '' }));
            }
        } else {
            setFormData(prev => ({ ...prev, vehicle: undefined }));
        }
    }, [selectedVehicle, validationErrors.vehicle]);

    useEffect(() => {
        vehicleStore.loadVehicles();
    }, [vehicleStore]);

    const handleUserInputChange = (field: keyof typeof formData.user) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ 
            ...prev, 
            user: { 
                ...prev.user, 
                [field]: value 
            } 
        }));

        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
        if (driverStore.error) {
            driverStore.clearError();
        }
    };

    const handleInputChange = (field: keyof Omit<DriverFormData, 'user'>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
        if (driverStore.error) {
            driverStore.clearError();
        }
    };

    const handleSelectChange = (field: keyof DriverFormData) => (event: any) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccessMessage('');

        const validation = driverStore.validateDriverForm(formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return;
        }

        const success = await driverStore.createDriver(formData);

        if (success) {
            setValidationErrors({});
            setSuccessMessage('Conductor registrado exitosamente');
            
            setFormData({
                user: {
                    username: '',
                    email: '',
                    first_name: '',
                    last_name: '',
                    phone_number: '',
                    profile_picture_url: ''
                },
                license_number: '',
                license_expiry: '',
                status: 'ACTIVE',
                vehicle: undefined
            });

            if (onSuccess) {
                setTimeout(onSuccess, 1500);
            }
        }
    };

    const getAvailableVehicles = () => {
        return vehicleStore.vehicles.filter(vehicle => vehicle.status === 'ACTIVE');
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 4,
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}
        >
            <Typography variant="h5" component="h1" fontWeight="500" mb={1}>
                Registrar Conductor
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={4}>
                Completa el formulario para registrar un nuevo conductor en el sistema.
            </Typography>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon fontSize="inherit" />}>
                    {successMessage}
                </Alert>
            )}

            {driverStore.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {driverStore.error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {/* User Information Section */}
                    <Typography variant="h6" fontWeight="600" color="primary.main">
                        Información Personal
                    </Typography>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Nombre *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: Juan"
                            value={formData.user.first_name}
                            onChange={handleUserInputChange('first_name')}
                            error={!!validationErrors.first_name}
                            helperText={validationErrors.first_name}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Apellido *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: Pérez"
                            value={formData.user.last_name}
                            onChange={handleUserInputChange('last_name')}
                            error={!!validationErrors.last_name}
                            helperText={validationErrors.last_name}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Nombre de Usuario *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: juan.perez"
                            value={formData.user.username}
                            onChange={handleUserInputChange('username')}
                            error={!!validationErrors.username}
                            helperText={validationErrors.username}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 30 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Email *
                        </Typography>
                        <TextField
                            fullWidth
                            type="email"
                            placeholder="ej: juan.perez@example.com"
                            value={formData.user.email}
                            onChange={handleUserInputChange('email')}
                            error={!!validationErrors.email}
                            helperText={validationErrors.email}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Teléfono *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: +58 412 123 4567"
                            value={formData.user.phone_number}
                            onChange={handleUserInputChange('phone_number')}
                            error={!!validationErrors.phone_number}
                            helperText={validationErrors.phone_number}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    {/* Driver Information Section */}
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mt: 2 }}>
                        Información de Conductor
                    </Typography>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Número de Licencia *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: 12345678"
                            value={formData.license_number}
                            onChange={handleInputChange('license_number')}
                            error={!!validationErrors.license_number}
                            helperText={validationErrors.license_number}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Fecha de Vencimiento de Licencia *
                        </Typography>
                        <TextField
                            fullWidth
                            type="date"
                            value={formData.license_expiry}
                            onChange={handleInputChange('license_expiry')}
                            error={!!validationErrors.license_expiry}
                            helperText={validationErrors.license_expiry}
                            disabled={driverStore.isSubmitting}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: getTodayDate() }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Estado
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={formData.status}
                                onChange={handleSelectChange('status')}
                                disabled={driverStore.isSubmitting}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }}
                            >
                                <MenuItem value="ACTIVE">Activo</MenuItem>
                                <MenuItem value="INACTIVE">Inactivo</MenuItem>
                                <MenuItem value="SUSPENDED">Suspendido</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Vehículo Asignado *
                        </Typography>
                        {selectedVehicle ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: '#f8f9fa',
                                    border: '2px solid #1976d2'
                                }}
                            >
                                <Box display="flex" alignItems="center">
                                    <Box sx={{ mr: 2 }}>
                                        <Typography variant="body1" fontWeight={600} color="primary.main">
                                            {selectedVehicle.license_plate}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedVehicle.brand} {selectedVehicle.model} • {selectedVehicle.year}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: validationErrors.vehicle ? '#ffebee' : '#fff3e0',
                                    border: validationErrors.vehicle ? '2px solid #f44336' : '2px dashed #ff9800'
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    color={validationErrors.vehicle ? 'error' : 'text.secondary'}
                                    textAlign="center"
                                >
                                    {validationErrors.vehicle || 'Selecciona un vehículo desde el panel lateral →'}
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={driverStore.isSubmitting}
                        sx={{
                            backgroundColor: '#1A1A1A',
                            color: 'white',
                            borderRadius: 1.5,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                            '&:disabled': {
                                backgroundColor: '#cccccc',
                            },
                            mt: 2
                        }}
                    >
                        {driverStore.isSubmitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                Registrando...
                            </>
                        ) : (
                            'Registrar Conductor'
                        )}
                    </Button>
                </Stack>
            </form>
        </Paper>
    );
});

export default DriverRegistrationForm;
